"use client";

import { useEffect, useMemo, useState } from "react";
import { usePathname } from "next/navigation";
import NoticeBanner from "@/components/common/NoticeBanner";
import { useAuth } from "@/components/AuthProvider";
import {
  dismissNoticeForSession,
  isNoticeDismissedForSession,
  resolveNoticeList,
} from "@/lib/notices/resolve-notices";

export default function NoticeBannerHost({ audience = "all" }) {
  const pathname = usePathname() || "/";
  const { user, profile, role, authStatus } = useAuth();
  const [mounted, setMounted] = useState(false);
  const [dismissedNoticeIds, setDismissedNoticeIds] = useState(() => new Set());
  const [platformNotices, setPlatformNotices] = useState([]);
  const [selectedNoticeId, setSelectedNoticeId] = useState(null);
  const suppressCustomerOnboardingBanner =
    audience === "customer" && pathname === "/customer/onboarding";

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return undefined;
    let cancelled = false;
    const controller = new AbortController();

    async function loadPlatformNotices() {
      try {
        const response = await fetch("/api/platform-announcements/active", {
          cache: "no-store",
          signal: controller.signal,
        });
        if (!response.ok) throw new Error("Unable to load announcement");
        const payload = await response.json();
        if (!cancelled) {
          setPlatformNotices(
            Array.isArray(payload?.notices)
              ? payload.notices
              : payload?.notice
                ? [payload.notice]
                : []
          );
        }
      } catch {
        if (!cancelled) setPlatformNotices([]);
      }
    }

    loadPlatformNotices();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [authStatus, mounted, role, user?.id]);

  const notices = useMemo(
    () =>
      resolveNoticeList({
        user,
        profile,
        role,
        pathname,
        extraNotices: platformNotices,
      }),
    [pathname, platformNotices, profile, role, user]
  );

  const visibleNotices = useMemo(
    () =>
      notices.filter(
        (notice) =>
          (notice.audience === "all" || notice.audience === audience || audience === "all") &&
          !dismissedNoticeIds.has(notice.id) &&
          !isNoticeDismissedForSession(notice.id)
      ),
    [audience, dismissedNoticeIds, notices]
  );

  const selectedIndex = Math.max(
    0,
    visibleNotices.findIndex((notice) => notice.id === selectedNoticeId)
  );
  const notice = visibleNotices[selectedIndex] || null;

  useEffect(() => {
    if (!notice) {
      if (selectedNoticeId !== null) setSelectedNoticeId(null);
      return;
    }
    if (selectedNoticeId !== notice.id) setSelectedNoticeId(notice.id);
  }, [notice, selectedNoticeId]);

  const visible =
    mounted &&
    !suppressCustomerOnboardingBanner &&
    authStatus !== "loading" &&
    notice;

  if (!visible) return null;

  return (
    <NoticeBanner
      {...notice}
      currentPosition={selectedIndex + 1}
      totalNotices={visibleNotices.length}
      onPrevious={() => {
        const previousIndex = (selectedIndex - 1 + visibleNotices.length) % visibleNotices.length;
        setSelectedNoticeId(visibleNotices[previousIndex]?.id || null);
      }}
      onNext={() => {
        const nextIndex = (selectedIndex + 1) % visibleNotices.length;
        setSelectedNoticeId(visibleNotices[nextIndex]?.id || null);
      }}
      onDismiss={
        notice.dismissible
          ? () => {
              dismissNoticeForSession(notice.id);
              setDismissedNoticeIds((prev) => new Set(prev).add(notice.id));
              setSelectedNoticeId(null);
            }
          : undefined
      }
    />
  );
}
